import Axios from 'axios';
import trim from 'lodash/trim';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import head from 'lodash/head';
import isUndefined from 'lodash/isUndefined';
import { userManager } from '../helpers/managers';
const { getStripeMinimumCharge } = require('../../server/scripts/utils')();

export const stripeSupportedCountries = [
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CA', name: 'Canada' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IN', name: 'India' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MX', name: 'Mexico' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NO', name: 'Norway' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
];

export const parseExpiryMonthAndYear = value => {
  const prefix = new Date().getFullYear().toString().substr(0, 2);

  // parse value coming from autofill
  if (isArray(value) && value[0].includes('/')) {
    value = value[0].split('/');
  }

  const [month, year] = value;
  return {
    expMonth: isEmpty(month) ? '' : Number(month),
    expYear: isEmpty(year) ? '' : Number(`${prefix}${year}`),
  };
};

export const createStripeCardSource = fields => {
  return new Promise((resolve, reject) => {
    if (isUndefined(window.Stripe)) {
      return reject(new Error('Missing Stripe instance'));
    }

    // removes leading and trailing whitespace
    for (const field in fields) {
      if (!isEmpty(fields[field])) {
        fields[field] = trim(fields[field]);
      }
    }

    const {
      addressLine1 = '',
      addressCity = '',
      addressState = '',
      addressZip = '',
      addressCountry = '',
      cardNumber = '',
      cardCVC = '',
      cardExpMonth = '',
      cardExpYear = '',
    } = fields;

    if (isEmpty(cardNumber)) {
      return reject(new Error('Card number is invalid.'));
    }

    if (isEmpty(cardCVC)) {
      return reject(new Error('Card CVC is invalid.'));
    }

    if (isNaN(cardExpMonth) || cardExpMonth === '') {
      return reject(new Error('Card Expiry month is invalid.'));
    }

    if (isNaN(cardExpYear) || cardExpYear === '') {
      return reject(new Error('Card Expiry year is invalid.'));
    }

    const source = {
      number: cardNumber,
      cvc: cardCVC,
      exp_month: Number(cardExpMonth),
      exp_year: Number(cardExpYear),
    };

    if (!isEmpty(addressLine1)) {
      source.address_line1 = addressLine1;
    }

    if (!isEmpty(addressCity)) {
      source.address_city = addressCity;
    }

    if (!isEmpty(addressState)) {
      source.address_state = addressState;
    }

    if (!isEmpty(addressZip)) {
      source.address_zip = addressZip;
    }

    if (!isEmpty(addressCountry)) {
      source.address_country = addressCountry;
    }

    window.Stripe.card.createToken(source, (status, response) => {
      if ('error' in response) {
        return reject(new Error(response.error.message));
      }

      resolve(response);
    });
  });
};

export const updateUserPaymentCard = options => {
  return new Promise((resolve, reject) => {
    const { source, customer, userId, email } = options;
    Axios.post('/api/updateCreditCard', {
      source,
      customer,
      userId,
      email,
    })
      .then(resolve)
      .catch(reject);
  });
};

export const checkPaymentCard = token => {
  return new Promise((resolve, reject) => {
    Axios.post('/api/checkCreditCardToken', {
      token,
    })
      .then(resolve)
      .catch(reject);
  });
};

export const updateUserBilling = options => {
  return new Promise((resolve, reject) => {
    const { source, customer, userId, billing } = options;
    const { addressLine1, addressCity, addressState, addressZip, addressCountry } = billing;

    Axios.post('/api/updateBilling', {
      source,
      customer,
      userId,
      details: {
        address_line1: addressLine1,
        address_city: addressCity,
        address_state: addressState,
        address_zip: addressZip,
        address_country: addressCountry,
      },
    })
      .then(resolve)
      .catch(reject);
  });
};

export const createStripeCustomer = (source, userId = '') => {
  return new Promise(async (resolve, reject) => {
    try {
      if (isEmpty(userId)) {
        throw new Error('Missing customer data.');
      }

      // retrieve user email
      const emailArray = await userManager.getProp(userId, 'email');
      if (isEmpty(emailArray)) {
        throw new Error('Customer email cannot be found.');
      }

      const email = head(emailArray);
      const result = await updateUserPaymentCard({
        source,
        userId,
        email,
      });

      resolve(result);
    } catch (err) {
      reject(err);
    }
  });
};

export const getDiscountedPriceFromCoupon = (price, coupon) => {
  const nprice = (Number(price) * (100 - coupon.percentOff)) / 100;
  return Number(nprice).toFixed(2);
};

export const hasDiscountsBelowMinimumPrice = (prices = []) => {
  if (isEmpty(prices)) {
    return false;
  }

  const with100PercentOff = coupon =>
    coupon.couponType === 'discount' && coupon.percentOff === '100';

  const higherThanMinimumPrice = (price, currency, coupon) => {
    if (with100PercentOff(coupon)) return true;
    const discounted = getDiscountedPriceFromCoupon(price, coupon);
    const minimum = getStripeMinimumCharge(currency);
    return discounted >= minimum;
  };

  const hasMinimumPrice = prices
    .map(price => ('coupons' in price ? [Number(price.price), price.currency, price.coupons] : []))
    .filter(discounts => !isEmpty(discounts))
    .some(discounts => {
      const [price, currency, coupons] = discounts;
      const found = coupons.find(coupon => !higherThanMinimumPrice(price, currency, coupon));
      return !isEmpty(found) ? true : false;
    });

  return hasMinimumPrice;
};

export const hasValidCouponCodes = (prices = []) => {
  if (isEmpty(prices)) {
    return true;
  }

  return prices
    .map(price => ('coupons' in price ? price.coupons : []))
    .filter(coupons => !isEmpty(coupons))
    .every(coupons =>
      coupons.every(coupon => {
        const conditions = [!isEmpty(coupon.code)];
        if (coupon.couponType === 'discount') {
          conditions.push(!isEmpty(coupon.percentOff));
        } else if (coupon.couponType === 'trialLength') {
          conditions.push(!isEmpty(coupon.trialLength));
        }

        return conditions.every(condition => !!condition);
      })
    );
};

// This returns the currency and the minimum possible price
// for the first of the elements of the prices array
// for which the price is set less than the minimum allowed.
// Only for "one time rental" allowed to set the price equal to 0.
export const findMinimumPriceIfPriceIsLessThanAllowed = (prices = []) => {
  return prices.reduce((accum, item) => {
    if (!isEmpty(accum)) {
      return accum;
    }

    if (item.billingCycle === 'rental' && Number(item.price) === 0) {
      return accum;
    }

    let currency = 'currency' in item ? item.currency : 'USD';
    let price = getStripeMinimumCharge(currency);
    if (!price) {
      currency = 'USD';
      price = getStripeMinimumCharge(currency);
    }

    if (Number(item.price) < price) {
      accum = { price, currency };
    }

    return accum;
  }, {});
};
